/**
 * Website enquiries: validated here for quick feedback, validated again and
 * stored by the submit-enquiry Edge Function (Turnstile, rate limits,
 * duplicate protection). Nothing opens the visitor's email app.
 */
import { callFunction, isBackendConfigured } from "./config";
import { enquiryTypes } from "../data/enquiries";

export const enquiryAvailable = () => isBackendConfigured();
export const findEnquiryType = (value) => enquiryTypes.find((t) => t.value === value) || enquiryTypes[0];

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const PHONE = /^[0-9+()\-\s.]{6,40}$/;

/** Local date as YYYY-MM-DD. */
export function localDate(d = new Date()) {
  const x = new Date(d);
  x.setMinutes(x.getMinutes() - x.getTimezoneOffset());
  return x.toISOString().slice(0, 10);
}

/** Returns { field: message } for every problem (empty object = valid). */
export function validateEnquiry(form, today = localDate()) {
  const type = findEnquiryType(form.type);
  const errors = {};
  const need = (f) => type.required.includes(f);
  if (!form.name?.trim()) errors.name = "Please enter your name.";
  else if (form.name.trim().length > 120) errors.name = "Please shorten your name.";
  if (!EMAIL.test(form.email?.trim() || "")) errors.email = "Please enter a valid email address.";
  if (form.phone?.trim() && !PHONE.test(form.phone.trim())) errors.phone = "Please check the phone number.";
  if (type.fields.includes("date")) {
    if (!form.date) {
      if (need("date")) errors.date = "Please choose a date.";
    } else if (form.date < today) errors.date = "Please choose a date from today onwards.";
  }
  if (need("time") && !form.time) errors.time = "Please choose a time.";
  if (type.fields.includes("guests")) {
    const n = Number(form.guests);
    if (form.guests === "" || form.guests === undefined) {
      if (need("guests")) errors.guests = "Please enter the number of guests.";
    } else if (!Number.isInteger(n) || n < 1 || n > type.maxGuests) {
      errors.guests = type.value === "table"
        ? `For more than ${type.maxGuests} guests, please choose Private Event.`
        : "Please enter a valid number of guests.";
    }
  }
  if (need("message") && !form.message?.trim()) errors.message = "Please tell us how we can help.";
  if ((form.message || "").length > 2000) errors.message = "Please keep your message under 2000 characters.";
  return errors;
}

class EnquiryError extends Error {
  constructor(code, detail) {
    super(code);
    this.code = code;
    this.detail = detail;
  }
}

/** Sends only the fields that belong to the chosen type. */
export async function submitEnquiry(form, { idempotencyKey, turnstileToken }) {
  const type = findEnquiryType(form.type);
  const has = (f) => type.fields.includes(f);
  const res = await callFunction("submit-enquiry", {
    type: type.value,
    name: form.name.trim(),
    email: form.email.trim(),
    phone: form.phone?.trim() || null,
    date: has("date") ? form.date || null : null,
    time: has("time") ? form.time || null : null,
    guests: has("guests") && form.guests !== "" ? Number(form.guests) : null,
    occasion: has("occasion") ? form.occasion || null : null,
    message: form.message?.trim() || null,
    event_slug: form.eventSlug || null,
    idempotency_key: idempotencyKey,
    turnstile_token: turnstileToken,
    website: form.website || "",
  });
  if (res.ok) return res.data;
  throw new EnquiryError(res.data?.error || `http_${res.status}`, res.data?.detail);
}

export function enquiryErrorMessage(err) {
  switch (err?.code) {
    case "invalid_input":
    case "invalid_enquiry":
      return "Please check the highlighted details and try again.";
    case "rate_limited":
      return "We have received several requests from you recently. Please wait a little, or contact us by email.";
    case "missing_token":
    case "invalid_token":
    case "token_expired_or_used":
    case "hostname_mismatch":
    case "action_mismatch":
    case "bot_check_failed":
      return "The security check expired or failed. Please complete it again, then send your request.";
    case "verification_unavailable":
    case "bot_protection_not_configured":
      return "Our form is temporarily unavailable. Please try again in a moment, or email us.";
    default:
      return "Your request could not be sent. Please try again, or email us.";
  }
}
